/*
 * Estimation des économies d'un particulier (CRM → Paramètres → Estimation des économies, clé « savings_estimate »).
 * Données du formulaire d'adhésion : consommation annuelle (kWh) et, si connu, montant payé par an (€ TVAC).
 * Calcul : prix par kWh = montant payé ÷ consommation, sinon prix par défaut ;
 *          économie annuelle = consommation × prix par kWh × taux d'économie, arrondie.
 * L'estimation est ajoutée à l'e-mail du contrat quand le réglage est activé.
 */
import { supabase } from '../../lib/supabase';
import type { CrmContact } from './types';

export interface SavingsEstimateSettings {
  /** Ajouter l'estimation à l'e-mail du contrat. */
  enabled: boolean;
  /** Prix tout compris (€/kWh TVAC) quand le montant payé n'est pas connu. */
  defaultPricePerKwh: number;
  /** Part de la facture économisée (0,15 = 15 %). */
  savingsRate: number;
  /** Arrondi de l'économie (€). */
  roundTo: number;
  /** Phrase ajoutée à l'e-mail du contrat ({{economie_estimee}}, {{consommation_annuelle}}, {{prix_kwh}}). */
  sentence: string;
}

export const DEFAULT_SAVINGS_SETTINGS: SavingsEstimateSettings = {
  enabled: false,
  defaultPricePerKwh: 0.4,
  savingsRate: 0.15,
  roundTo: 5,
  sentence: 'Sur la base de votre consommation annuelle de {{consommation_annuelle}}, nous estimons votre économie à environ {{economie_estimee}} par an.',
};

export const SAVINGS_VARIABLES: { key: string; label: string }[] = [
  { key: '{{economie_estimee}}', label: 'économie estimée (€/an)' },
  { key: '{{consommation_annuelle}}', label: 'consommation annuelle (kWh)' },
  { key: '{{prix_kwh}}', label: 'prix retenu (€/kWh)' },
];

const num = (v: unknown, fallback: number) => (typeof v === 'number' && Number.isFinite(v) ? v : fallback);

export function normalizeSavingsSettings(v: Partial<SavingsEstimateSettings> | null | undefined): SavingsEstimateSettings {
  const d = DEFAULT_SAVINGS_SETTINGS;
  return {
    enabled: typeof v?.enabled === 'boolean' ? v.enabled : d.enabled,
    defaultPricePerKwh: num(v?.defaultPricePerKwh, d.defaultPricePerKwh),
    savingsRate: num(v?.savingsRate, d.savingsRate),
    roundTo: num(v?.roundTo, d.roundTo),
    sentence: typeof v?.sentence === 'string' && v.sentence.trim() ? v.sentence : d.sentence,
  };
}

export function savingsSettingsErrors(v: SavingsEstimateSettings): Partial<Record<keyof SavingsEstimateSettings, string>> {
  const e: Partial<Record<keyof SavingsEstimateSettings, string>> = {};
  if (!(v.defaultPricePerKwh > 0 && v.defaultPricePerKwh < 5)) e.defaultPricePerKwh = 'Prix entre 0 et 5 €/kWh';
  if (!(v.savingsRate > 0 && v.savingsRate < 1)) e.savingsRate = 'Pourcentage entre 0 et 100';
  if (!(v.roundTo >= 1 && v.roundTo <= 100)) e.roundTo = 'Entre 1 et 100 €';
  if (!v.sentence.includes('{{economie_estimee}}')) e.sentence = 'La phrase doit contenir {{economie_estimee}}';
  return e;
}

let cache: SavingsEstimateSettings | null = null;

export async function fetchSavingsSettings(force = false): Promise<SavingsEstimateSettings> {
  if (cache && !force) return cache;
  const { data, error } = await supabase.from('crm_settings').select('value').eq('key', 'savings_estimate').maybeSingle();
  if (error) throw error;
  cache = normalizeSavingsSettings((data as { value?: Partial<SavingsEstimateSettings> } | null)?.value);
  return cache;
}

export async function saveSavingsSettings(value: SavingsEstimateSettings): Promise<void> {
  const { error } = await supabase.from('crm_settings')
    .upsert({ key: 'savings_estimate', value, updated_at: new Date().toISOString() }, { onConflict: 'key' });
  if (error) throw error;
  cache = value;
}

export interface SavingsEstimate {
  consumptionKwh: number;
  pricePerKwh: number;
  /** Prix tiré du montant payé déclaré, ou prix par défaut des réglages. */
  priceSource: 'declared' | 'default';
  annualSavings: number;
}

type EstimateInput = Pick<CrmContact, 'annual_consumption_kwh' | 'annual_cost_eur'>;

/** Calcul seul (aperçu des réglages, fiche) ; null sans consommation. */
export function computeSavings(input: EstimateInput, s: SavingsEstimateSettings): SavingsEstimate | null {
  const kwh = Number(input.annual_consumption_kwh);
  if (!(kwh > 0)) return null;
  const cost = Number(input.annual_cost_eur);
  const declared = cost > 0;
  const pricePerKwh = declared ? cost / kwh : s.defaultPricePerKwh;
  const raw = kwh * pricePerKwh * s.savingsRate;
  const step = s.roundTo > 0 ? s.roundTo : 1;
  return { consumptionKwh: kwh, pricePerKwh, priceSource: declared ? 'declared' : 'default', annualSavings: Math.round(raw / step) * step };
}

/** Estimation à envoyer avec le contrat : demandée, particulier consommateur ou prosumer, consommation connue. */
export function contractEstimate(
  c: Pick<CrmContact, 'estimate_requested' | 'entity_type' | 'role' | 'annual_consumption_kwh' | 'annual_cost_eur'>,
  s: SavingsEstimateSettings,
): SavingsEstimate | null {
  if (!c.estimate_requested || c.entity_type !== 'physique' || (c.role !== 'consumer' && c.role !== 'prosumer')) return null;
  return computeSavings(c, s);
}

export const formatEuros = (v: number) => `${v.toLocaleString('fr-BE', { maximumFractionDigits: 0 })} €`;
export const formatKwh = (v: number) => `${v.toLocaleString('fr-BE', { maximumFractionDigits: 0 })} kWh`;
export const formatPrice = (v: number) => `${v.toLocaleString('fr-BE', { minimumFractionDigits: 2, maximumFractionDigits: 3 })} €/kWh`;

/** Valeurs des variables de l'estimation (e-mail du contrat). */
export function savingsVariables(e: SavingsEstimate | null): Record<string, string> {
  return {
    '{{economie_estimee}}': e ? formatEuros(e.annualSavings) : '',
    '{{consommation_annuelle}}': e ? formatKwh(e.consumptionKwh) : '',
    '{{prix_kwh}}': e ? formatPrice(e.pricePerKwh) : '',
  };
}

export function renderSavingsSentence(sentence: string, e: SavingsEstimate): string {
  return Object.entries(savingsVariables(e)).reduce((acc, [k, v]) => acc.split(k).join(v), sentence);
}
